/**
 * Release Run Selection
 *
 * @module features/releases/releaseRunSelection
 * @description Pure helper for the timeline's master-detail layout: which run
 * the detail pane shows when the URL carries no explicit `?runId=`. No React,
 * no DOM — safe to unit test.
 */

import { isActiveStatus, type ReleaseQARunStatus } from './releaseTypes';
import {
  isBuildEntry,
  isCommitEntry,
  isRunEntry,
  type TimelineCommitEntry,
  type TimelineEntry,
} from './hooks/useReleaseTimeline';

/**
 * The default selected run for a timeline: the first (newest, since entries
 * are newest-first) run entry that's still active, else the first run entry
 * overall, else (a pushed-image application's timeline, which has no runs) the
 * newest build a suite ran on, else `null`. This is a
 * derived default — never written to the URL — so an explicit `?runId=`
 * always wins over it.
 */
export function pickDefaultRunId(entries: TimelineEntry[]): string | null {
  const runs = entries.filter(isRunEntry);
  const active = runs.find((run) => isActiveStatus(run.status as ReleaseQARunStatus));
  const firstTestedBuild = entries.find((entry) => isBuildEntry(entry) && entry.executionId !== null);
  return active?.id ?? runs[0]?.id ?? firstTestedBuild?.id ?? null;
}

/**
 * Pairs each run entry with the commit it actually tested up to, by SHA, so
 * `ReleaseTimeline` can render that commit inside the run's band instead of
 * as its own row.
 *
 * A run whose `toSha` (or per-repo checkpoint `toSha`) is not among the
 * loaded commits — it may sit on a later page, or predate the window —
 * simply gets no entry in the map.
 */
export function buildReleasedCommitMap(entries: TimelineEntry[]): Map<string, TimelineCommitEntry> {
  const commitBySha = new Map<string, TimelineCommitEntry>();
  for (const entry of entries) {
    if (isCommitEntry(entry)) commitBySha.set(entry.sha, entry);
  }
  const map = new Map<string, TimelineCommitEntry>();
  for (const entry of entries) {
    if (!isRunEntry(entry)) continue;
    const shas = [...(entry.toSha ? [entry.toSha] : []), ...entry.repoCheckpoints.map((cp) => cp.toSha)];
    for (const sha of shas) {
      const commit = commitBySha.get(sha);
      if (commit) {
        map.set(entry.id, commit);
        break;
      }
    }
  }
  return map;
}
